import React, { useState, useEffect } from 'react';
import { fetchTechniciansByTechService, fetchTechnicianAvailability } from '../services/technicianAvailabilityService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import './TechnicianSelectModal.css';

const TechnicianSelectModal = ({
  isOpen,
  onClose,
  onSubmit,
  appointmentDate,
  appointmentTime,
  techServiceId,
}) => {
  const { confirm } = useConfirmDialog();
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTechnicianId, setSelectedTechnicianId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && techServiceId && appointmentDate && appointmentTime) {
      loadTechnicians();
    }
  }, [isOpen, techServiceId, appointmentDate, appointmentTime]);

  const loadTechnicians = async () => {
    setLoading(true);
    setError('');
    try {
      // Teknik servise ait teknisyenleri getir
      const response = await fetchTechniciansByTechService(techServiceId);
      const allTechnicians = response?.veri?.teknisyenler || response?.data?.teknisyenler || [];

      // Her teknisyen için müsaitlik durumunu kontrol et
      const techniciansWithAvailability = await Promise.all(
        allTechnicians.map(async (tech) => {
          try {
            const availResponse = await fetchTechnicianAvailability(tech._id || tech.id, {
              type: 'all',
              startDate: appointmentDate,
              endDate: appointmentDate,
            });

            // Backend'den gelen kuralları kontrol et
            const rules = availResponse?.veri?.kurallar || [];
            
            // Bu tarih ve saatte OFF olan kural var mı?
            const isOff = rules.some((rule) => {
              if (rule.tumGun) {
                return true; // Tüm gün OFF
              }
              if (Array.isArray(rule.saatler) && rule.saatler.includes(appointmentTime)) {
                return true; // Bu saat OFF
              }
              return false;
            });

            return {
              ...tech,
              isAvailable: !isOff,
              offReason: isOff ? (rules.find(r => r.tumGun)?.tumGun ? 'Tüm Gün OFF' : 'Bu Saatte OFF') : null,
            };
          } catch (err) {
            console.error(`Teknisyen ${tech._id} müsaitlik kontrolü hatası:`, err);
            return {
              ...tech,
              isAvailable: true, // Hata durumunda müsait kabul et
              offReason: null,
            };
          }
        })
      );

      setTechnicians(techniciansWithAvailability);
    } catch (err) {
      console.error('Teknisyen listesi yükleme hatası:', err);
      setError('Teknisyenler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedTechnicianId) {
      window.showNotification?.('warning', 'Lütfen bir teknisyen seçiniz');
      return;
    }

    if (!reason.trim()) {
      window.showNotification?.('warning', 'Lütfen bir gerekçe giriniz');
      return;
    }

    const selectedTech = technicians.find(t => (t._id || t.id) === selectedTechnicianId);
    
    if (selectedTech && !selectedTech.isAvailable) {
      const ok = await confirm({
        title: 'Teknisyen müsait değil',
        message: 'Seçtiğiniz teknisyen bu saatte OFF durumunda. Yine de atamak istiyor musunuz?',
        variant: 'warning',
        confirmLabel: 'Yine de ata',
      });
      if (!ok) return;
    }

    const submitData = {
      teknisyenId: selectedTechnicianId,
      gerekce: reason,
    };

    setSubmitting(true);
    try {
      await onSubmit(submitData);
      // Başarılı olursa modal'ı kapat
      handleClose();
    } catch (err) {
      // Hata parent component tarafından handle edilecek
      console.error('Teknisyen atama hatası:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedTechnicianId('');
    setReason('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const availableTechnicians = technicians.filter(t => t.isAvailable);
  const offTechnicians = technicians.filter(t => !t.isAvailable);

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="technician-select-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            <i className="fas fa-user-plus"></i>
            Teknisyen Ata
          </h3>
          <button className="close-btn" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {/* Randevu Bilgisi */}
          <div className="appointment-info">
            <i className="fas fa-calendar-alt"></i>
            <span>
              Randevu: {appointmentDate ? new Date(appointmentDate).toLocaleDateString('tr-TR') : '---'} - {appointmentTime || '---'}
            </span>
          </div>

          {loading ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Teknisyenler yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <i className="fas fa-exclamation-circle"></i>
              <p>{error}</p>
              <button onClick={loadTechnicians} className="retry-btn">
                Tekrar Dene
              </button>
            </div>
          ) : (
            <>
              {/* Müsait Teknisyenler */}
              {availableTechnicians.length > 0 && (
                <div className="technicians-section">
                  <h4>
                    <i className="fas fa-check-circle"></i>
                    Müsait Teknisyenler ({availableTechnicians.length})
                  </h4>
                  <div className="technicians-list">
                    {availableTechnicians.map((tech) => {
                      const techId = tech._id || tech.id;
                      const techName = `${tech.ad || ''} ${tech.soyad || ''}`.trim();
                      const isSelected = selectedTechnicianId === techId;

                      return (
                        <div
                          key={techId}
                          className={`technician-card available ${isSelected ? 'selected' : ''}`}
                          onClick={() => setSelectedTechnicianId(techId)}
                        >
                          <div className="tech-info">
                            <div className="tech-name">
                              {isSelected && <i className="fas fa-check-circle"></i>}
                              {techName || 'İsimsiz'}
                            </div>
                            {tech.telefon && (
                              <div className="tech-phone">
                                <i className="fas fa-phone"></i>
                                {tech.telefon}
                              </div>
                            )}
                          </div>
                          <div className="availability-badge available">
                            <i className="fas fa-check"></i>
                            Müsait
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* OFF Teknisyenler */}
              {offTechnicians.length > 0 && (
                <div className="technicians-section off-section">
                  <h4>
                    <i className="fas fa-ban"></i>
                    OFF Durumunda ({offTechnicians.length})
                  </h4>
                  <div className="technicians-list">
                    {offTechnicians.map((tech) => {
                      const techId = tech._id || tech.id;
                      const techName = `${tech.ad || ''} ${tech.soyad || ''}`.trim();
                      const isSelected = selectedTechnicianId === techId;

                      return (
                        <div
                          key={techId}
                          className={`technician-card off ${isSelected ? 'selected' : ''}`}
                          onClick={() => setSelectedTechnicianId(techId)}
                        >
                          <div className="tech-info">
                            <div className="tech-name">
                              {isSelected && <i className="fas fa-check-circle"></i>}
                              {techName || 'İsimsiz'}
                            </div>
                            {tech.telefon && (
                              <div className="tech-phone">
                                <i className="fas fa-phone"></i>
                                {tech.telefon}
                              </div>
                            )}
                          </div>
                          <div className="availability-badge off">
                            <i className="fas fa-times"></i>
                            {tech.offReason || 'OFF'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {technicians.length === 0 && (
                <div className="empty-state">
                  <i className="fas fa-users-slash"></i>
                  <p>Bu teknik servise ait teknisyen bulunamadı</p>
                </div>
              )}

              {/* Gerekçe */}
              <div className="reason-section">
                <label>Gerekçe *</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Atama gerekçesini giriniz..."
                  rows="3"
                  maxLength="500"
                />
                <small>{reason.length}/500 karakter</small>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="cancel-btn"
            onClick={handleClose}
            disabled={submitting}
          >
            İptal
          </button>
          <button
            type="button"
            className="submit-btn"
            onClick={handleSubmit}
            disabled={loading || submitting || !selectedTechnicianId || !reason.trim()}
          >
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>Atanıyor...</span>
              </>
            ) : (
              <>
                <i className="fas fa-user-check"></i>
                <span>Teknisyen Ata</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TechnicianSelectModal;
